import React from 'react';
import { Button, Typography, Space, Empty } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined, EyeOutlined, DeleteOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

const { Text } = Typography;

interface BackgroundTaskListProps {
  tasks: any[];
  onView: (task: any) => void;
  onClose: (task: any) => void;
  onDrawerClose: () => void;
}

export const BackgroundTaskList: React.FC<BackgroundTaskListProps> = ({ tasks, onView, onClose, onDrawerClose }) => {
  const navigate = useNavigate();

  const handleGoTo = (task: any) => {
    if (task.navigationUrl) {
      onDrawerClose();
      if (window.location.pathname === task.navigationUrl) {
        window.location.reload();
      } else {
        navigate(task.navigationUrl);
      }
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'generating':
        return <LoadingOutlined spin style={{ color: 'var(--plat-accent)' }} />;
      case 'done':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'error':
        return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
      default:
        return null;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'generating':
        return '生成中';
      case 'done':
        return '已完成';
      case 'error':
        return '失败';
      default:
        return '';
    }
  };

  if (tasks.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--plat-text-hint)' }}>
        <Empty description="暂无任务" />
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: 'var(--plat-text-hint)' }}>刷新页面将丢失任务</div>
      </div>
      {tasks.map((task) => (
        <div
          key={task.id}
          style={{
            padding: '8px 12px',
            background: 'var(--plat-bg-card)',
            border: '1px solid var(--plat-border)',
            borderRadius: 'var(--plat-radius-sm)',
            marginBottom: 8,
            transition: 'all 0.2s',
            cursor: 'pointer',
            position: 'relative',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.boxShadow = 'var(--plat-shadow)';
            e.currentTarget.style.borderColor = 'var(--plat-accent-light)';
            const deleteBtn = e.currentTarget.querySelector('.delete-btn') as HTMLElement;
            if (deleteBtn) deleteBtn.style.opacity = '1';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.boxShadow = 'none';
            e.currentTarget.style.borderColor = 'var(--plat-border)';
            const deleteBtn = e.currentTarget.querySelector('.delete-btn') as HTMLElement;
            if (deleteBtn) deleteBtn.style.opacity = '0';
          }}
        >
          <Button
            type="text"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              onClose(task);
            }}
            className="delete-btn"
            style={{
              position: 'absolute',
              top: 4,
              right: 4,
              opacity: 0,
              transition: 'opacity 0.2s',
              padding: '0 4px',
              fontSize: 14,
            }}
          />
          <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 8 }}>
            {getStatusIcon(task.status)}
            <div style={{ flex: 1, marginLeft: 12 }}>
              <div>
                <Text strong>{task.resourceName}</Text>
              </div>
              <div style={{ marginTop: 4 }}>
                <Space size={8}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {new Date(task.createdAt).toLocaleString('zh-CN', {
                      year: 'numeric',
                      month: 'numeric',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit',
                      hour12: false,
                    })}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {getStatusText(task.status)}
                  </Text>
                </Space>
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 4 }}>
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => onView(task)}
              style={{ fontSize: 12, padding: '0 4px' }}
            >
              查看
            </Button>
            {task.navigationUrl && (
              <Button
                type="text"
                size="small"
                icon={<ArrowRightOutlined />}
                onClick={() => handleGoTo(task)}
                style={{ fontSize: 12, padding: '0 4px' }}
              >
                前往
              </Button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};
